import { FilterChip } from '@src/components/shared/FilterChip';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Card, CardContent } from '@src/components/ui/card';
import { Input } from '@src/components/ui/input';
import {
  COVERAGE_SEGMENT_KEYS,
  type CoverageSegmentKey,
  coverageSegmentLabel,
  FALLBACK_OUTCOME_COLOR,
  OUTCOME_COLOR,
} from '@src/lib/coverage';
import { errorMessage } from '@src/lib/errors';
import { cn } from '@src/lib/utils';
import {
  buildOverviewTile,
  buildTopStats,
  type CoverageSegment,
  type OverviewTileViewModel,
  statusDotClass,
  statusTextClass,
} from '@src/pages/Overview.utils';
import { resources } from '@src/resources';
import { type CoverageRow, fetchCoverage, fetchStats, type FetchStatsParams, type StatsRow } from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

type WindowSelection = 'today' | '7d' | '30d' | 'all' | 'custom';

const DEFAULT_WINDOW: WindowSelection = 'all';

const WINDOW_OPTIONS: { value: WindowSelection; label: string }[] = [
  { value: 'today', label: resources.overview.windowOptions.today },
  { value: '7d', label: resources.overview.windowOptions['7d'] },
  { value: '30d', label: resources.overview.windowOptions['30d'] },
  { value: 'all', label: resources.overview.windowOptions.all },
  { value: 'custom', label: resources.overview.windowOptions.custom },
];

export function Overview() {
  // The window filter lives in the URL (not useState), matching the Runs/Enrichments screens --
  // shareable/bookmarkable, survives a refresh or back/forward navigation.
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = (key: string, value: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  const dateWindow = (searchParams.get('window') as WindowSelection | null) ?? DEFAULT_WINDOW;
  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';

  const setDateWindow = (value: WindowSelection) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_WINDOW) {
          next.delete('window');
        } else {
          next.set('window', value);
        }
        if (value !== 'custom') {
          next.delete('from');
          next.delete('to');
        }
        return next;
      },
      { replace: true },
    );
  };
  const setCustomFrom = (value: string) => setParam('from', value);
  const setCustomTo = (value: string) => setParam('to', value);

  const isCustomWindow = dateWindow === 'custom';
  // datetime-local gives a value with no timezone offset (interpreted as local time by `new Date()`,
  // which is what we want -- the picker itself is already showing the user's local time).
  const customSince = isCustomWindow && customFrom ? new Date(customFrom).toISOString() : undefined;
  const customUntil = isCustomWindow && customTo ? new Date(customTo).toISOString() : undefined;

  const statsParams: FetchStatsParams = {
    window: isCustomWindow ? undefined : dateWindow,
    since: customSince,
    until: customUntil,
  };

  // Unlike /ops/runs and /ops/enrichments, /ops/stats requires window OR since -- an empty custom
  // range isn't a valid "unfiltered" request there, it's a 400. Rather than firing a request that
  // can only fail, the query stays disabled (and a prompt shown instead) until a start date is set.
  const isCustomWithoutRange = isCustomWindow && customSince === undefined;

  const statsQuery = useQuery({
    queryKey: ['ops', 'stats', statsParams],
    queryFn: () => fetchStats(statsParams),
    enabled: !isCustomWithoutRange,
  });
  const coverageQuery = useQuery({
    queryKey: ['ops', 'coverage'],
    queryFn: fetchCoverage,
  });

  const isLoading = !isCustomWithoutRange && (statsQuery.isPending || coverageQuery.isPending);
  const isError = !isCustomWithoutRange && (statsQuery.isError || coverageQuery.isError);
  // Both queries can fail independently -- show whichever error(s) actually happened rather than
  // picking one arbitrarily.
  const errorDetail = [statsQuery.error, coverageQuery.error].filter(Boolean).map(errorMessage).join(' · ');

  return (
    <div>
      <PageHeader
        title={resources.pages.overview.title}
        subtitle={resources.pages.overview.subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-1.5">
            {WINDOW_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={dateWindow === opt.value}
                onClick={() => setDateWindow(opt.value)}
              />
            ))}
          </div>
        }
      />

      {isCustomWindow && (
        <div className="mb-4 flex items-center gap-1.5">
          <Input
            type="datetime-local"
            value={customFrom}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomFrom(event.target.value)}
            className="w-[190px]"
            aria-label={resources.overview.customFromLabel}
          />
          <span className="text-xs text-muted-foreground">{resources.overview.customRangeSeparator}</span>
          <Input
            type="datetime-local"
            value={customTo}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomTo(event.target.value)}
            className="w-[190px]"
            aria-label={resources.overview.customToLabel}
          />
        </div>
      )}

      {isCustomWithoutRange && (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">{resources.overview.customRangePrompt}</CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">{resources.common.loading}</CardContent>
        </Card>
      )}

      {!isLoading && isError && (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">
            <p>{resources.common.loadError}</p>
            <p className="mt-1 font-mono text-xs text-status-critical">{errorDetail}</p>
          </CardContent>
        </Card>
      )}

      {!isCustomWithoutRange && !isLoading && !isError && (
        <OverviewContent statsRows={statsQuery.data?.items ?? []} coverageRows={coverageQuery.data?.items ?? []} />
      )}
    </div>
  );
}

interface OverviewContentProps {
  statsRows: StatsRow[];
  coverageRows: CoverageRow[];
}

function OverviewContent({ statsRows, coverageRows }: OverviewContentProps) {
  const coverageByScope = new Map(coverageRows.map((row) => [row.scope, row]));
  const topStats = buildTopStats(statsRows);
  const tiles: OverviewTileViewModel[] = statsRows.map((row) => buildOverviewTile(row, coverageByScope.get(row.scope)));

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={resources.overview.stats.totalCost} value={topStats.totalCostLabel} />
        <StatCard label={resources.overview.stats.totalRuns} value={topStats.totalRunsLabel} />
        <StatCard label={resources.overview.stats.successRate} value={topStats.overallRateLabel} />
        <StatCard
          label={resources.overview.stats.attention}
          value={String(topStats.attentionCount)}
          emphasis={topStats.attentionCount > 0}
        />
      </div>

      <Legend />

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((tile) => (
          <ScopeTile key={tile.scope} tile={tile} />
        ))}
      </div>
    </div>
  );
}

/** Explains the status-badge thresholds and coverage-bar colors used on every tile below --
 * shown once here rather than repeated per tile. Thresholds mirror statusForRate()
 * (Overview.utils.ts); coverage dots are built from lib/coverage's COVERAGE_SEGMENT_KEYS (the
 * three record-store outcomes plus the real NEVER_RUN bucket), so a new record-store-backed
 * outcome shows up here with no change to this component. */
function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[10.5px] text-muted-foreground">
      <span className="font-medium uppercase tracking-wide">{resources.overview.legend.title}</span>
      <LegendDot colorClass="bg-status-good" label={resources.overview.legend.statusGood} />
      <LegendDot colorClass="bg-status-warning" label={resources.overview.legend.statusWarning} />
      <LegendDot colorClass="bg-status-critical" label={resources.overview.legend.statusCritical} />
      <span className="text-border">|</span>
      <span>{resources.overview.legend.coveragePrefix}</span>
      {COVERAGE_SEGMENT_KEYS.map((key) => (
        <LegendDot
          key={key}
          colorClass={OUTCOME_COLOR[key] ?? FALLBACK_OUTCOME_COLOR}
          label={coverageSegmentLabel(key)}
        />
      ))}
    </div>
  );
}

function LegendDot({ colorClass, label }: { colorClass: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('h-2 w-2 shrink-0 rounded-full', colorClass)} />
      {label}
    </span>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  emphasis?: boolean;
}

function StatCard({ label, value, emphasis }: StatCardProps) {
  return (
    <Card className={cn('p-4', emphasis && 'border-status-critical')}>
      <div className="mb-2 text-[11.5px] text-muted-foreground">{label}</div>
      <div className={cn('text-2xl font-semibold tabular-nums', emphasis ? 'text-status-critical' : 'text-foreground')}>
        {value}
      </div>
    </Card>
  );
}

function ScopeTile({ tile }: { tile: OverviewTileViewModel }) {
  // tile.scope is the real EnrichmentScope enum value (e.g. "AGENT_SOURCE_LISTING") straight from
  // the API -- passed through as-is, since /ops/runs?scope= does an exact match against that same
  // casing server-side. (The Runs screen's own scope <Select> still offers kebab-case options,
  // a separate known casing mismatch -- this link works regardless, it just won't show as
  // "selected" in that dropdown.)
  return (
    <Link
      to={`/runs?scope=${encodeURIComponent(tile.scope)}`}
      aria-label={`View ${tile.label} runs`}
      className="block rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <Card className="flex h-full flex-col gap-2.5 p-4 transition-colors hover:border-primary/50">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">{tile.label}</div>
          <div className="flex items-center gap-1.5">
            <span className={cn('h-[7px] w-[7px] rounded-full', statusDotClass(tile.status))} />
            <span className={cn('text-[10.5px] font-semibold', statusTextClass(tile.status))}>
              {resources.overview.status[tile.status]}
            </span>
          </div>
        </div>

        <div className="flex items-baseline gap-1.5">
          <div className="text-[25px] font-semibold tabular-nums">{tile.rateLabel}</div>
          <div className="text-[10.5px] text-muted-foreground">{resources.overview.tile.successSuffix}</div>
          <div
            className="ml-auto flex items-baseline gap-1"
            title={resources.overview.tile.coveredTooltip}
          >
            <span className="text-[13px] font-semibold tabular-nums text-muted-foreground">
              {tile.coveragePctLabel}
            </span>
            <span className="text-[10px] text-muted-foreground">{resources.overview.tile.coveredSuffix}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2 border-t border-border pt-2.5 text-[11px]">
          <TileMetric label={resources.overview.tile.runs} value={tile.runsLabel} />
          <TileMetric label={resources.overview.tile.spend} value={tile.costLabel} />
          <TileMetric label={resources.overview.tile.avgLatency} value={tile.avgLatencyLabel} />
          <TileMetric label={resources.overview.tile.lastRun} value={tile.lastRunLabel} />
        </div>

        <CoverageBar segments={tile.segments} />
      </Card>
    </Link>
  );
}

function TileMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

/** "Updated 43 (86%)" -- shared between the whole-bar aria-label and each segment's own tooltip. */
function segmentSummary(segment: CoverageSegment): string {
  return `${segment.label}: ${segment.count.toLocaleString()} (${segment.pct.toFixed(0)}%)`;
}

function CoverageBar({ segments: allSegments }: { segments: CoverageSegment[] }) {
  // Native `title` tooltips are slow (~1s delay) and easy to miss on a 2px-tall bar -- a small
  // custom popover shows instantly and reliably instead. `hoveredOutcome` is local to this bar
  // instance (one CoverageBar per tile), so hovering one tile never affects another.
  const [hoveredOutcome, setHoveredOutcome] = useState<CoverageSegmentKey | null>(null);
  const segments = allSegments.filter((segment) => segment.count > 0);
  const ariaLabel = segments.map(segmentSummary).join(', ');
  const hoveredSegment = segments.find((segment) => segment.outcome === hoveredOutcome);

  return (
    <div className="flex items-center gap-2">
      <div className="shrink-0 text-[9.5px] text-muted-foreground">{resources.overview.tile.coverage}</div>
      <div className="relative flex-1">
        {hoveredSegment && (
          <div
            role="tooltip"
            className="absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-card px-2 py-1 text-[10.5px] font-medium text-foreground shadow-lg"
          >
            {segmentSummary(hoveredSegment)}
          </div>
        )}
        <div
          className="flex h-2 gap-px overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={ariaLabel}
        >
          {segments.map((segment) => (
            <div
              key={segment.outcome}
              className={cn(segment.colorClass, 'transition-[filter] duration-100 hover:brightness-125')}
              style={{ flex: `${segment.pct} 0 0%` }}
              onMouseEnter={() => setHoveredOutcome(segment.outcome)}
              onMouseLeave={() => setHoveredOutcome((current) => (current === segment.outcome ? null : current))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
